import { formatLargeNumber, getNumberAsLocaleNumber, getVar, isColorDark } from '@Sage/ssm-utils';

import { configureChartDefaults } from 'lib/configureChartDefaults';

import { CHART_DEFAULTS } from './constants';

export const createCopilotChartRuntimeLoader = (load) => {
    let runtimePromise;

    return () => {
        if (!runtimePromise) {
            runtimePromise = load()
                .then(([chartModule, dataLabelsModule]) => {
                    configureChartDefaults(chartModule.default.defaults);
                    return {
                        Chart: chartModule.default,
                        ChartDataLabels: dataLabelsModule.default,
                    };
                })
                .catch((error) => {
                    runtimePromise = undefined;
                    throw error;
                });
        }

        return runtimePromise;
    };
};

export const loadCopilotChartRuntime = createCopilotChartRuntimeLoader(() =>
    Promise.all([import('chart.js/auto'), import('chartjs-plugin-datalabels')]),
);

export const createCopilotChart = ({
    Chart,
    ChartDataLabels,
    colors,
    data,
    id,
    onImage,
    parent,
}) => {
    let chart;
    const chartConfig = {
        ...data,
        options: {
            ...(data.options || {}),
            devicePixelRatio: CHART_DEFAULTS.devicePixelRatio,
            responsive: true,
            layout: {
                ...(data.options?.layout || {}),
                padding: CHART_DEFAULTS.padding,
            },
            animation: {
                onComplete: () => onImage(id, chart.toBase64Image()),
            },
            plugins: {
                customCanvasBackgroundColor: {
                    color: getVar(colors.actionMinor[50]),
                },
                tooltip: {
                    enabled: true,
                    callbacks: {
                        title: (tooltipItem) => tooltipItem.label,
                        label: (tooltipItem) => {
                            let value = tooltipItem.dataset.data?.[tooltipItem.dataIndex];
                            if (isNaN(value)) return '';
                            value = getNumberAsLocaleNumber(value.toString());
                            const label = tooltipItem?.dataset?.label || '';
                            return label ? `${label}: ${value}` : value;
                        },
                    },
                    boxPadding: 4,
                },
                datalabels: {
                    color: (context) => {
                        const backgroundColors = context?.dataset?.backgroundColor || [];
                        const backgroundColor = backgroundColors?.[context.dataIndex];
                        const isChartPie = ['pie', 'doughnut'].includes(data.type);

                        if (!backgroundColor?.length || !isChartPie) {
                            return getVar(colors.grey[700]);
                        }

                        const darks = backgroundColors.filter((current) => {
                            const rgbArray = current
                                .match(/\d+/g)
                                ?.map((value) => parseInt(value, 10));
                            return rgbArray
                                ? isColorDark(rgbArray, {
                                      grayscaleWeights: CHART_DEFAULTS.grayscaleWeights,
                                      threshold: CHART_DEFAULTS.colorThreshold,
                                  })
                                : false;
                        });

                        return darks.length >= Math.ceil(backgroundColors.length / 2)
                            ? getVar(colors.primary.white)
                            : getVar(colors.grey[700]);
                    },
                    font: {
                        weight: ['pie', 'doughnut'].includes(data.type) ? 'bold' : 'normal',
                    },
                    anchor: 'center',
                    formatter: (value, context) => {
                        if (!value) return null;
                        const hasMultipleDatasets = data.data.datasets.length > 1;
                        return hasMultipleDatasets
                            ? `${context.dataset.label}\n${formatLargeNumber(value)}`
                            : formatLargeNumber(value);
                    },
                    textAlign: 'center',
                    display: 'auto',
                },
            },
        },
        plugins: [
            ChartDataLabels,
            {
                id: 'customCanvasBackgroundColor',
                beforeDraw: (currentChart, args, options) => {
                    const { ctx } = currentChart;
                    ctx.save();
                    ctx.globalCompositeOperation = 'destination-over';
                    ctx.fillStyle = options.color || getVar(colors.actionMinor[50]);
                    ctx.fillRect(0, 0, currentChart.width, currentChart.height);
                    ctx.restore();
                },
            },
        ],
    };

    const canvas = document.createElement('canvas');
    canvas.width = CHART_DEFAULTS.width;
    canvas.height = CHART_DEFAULTS.height;
    parent.appendChild(canvas);
    chart = new Chart(canvas.getContext('2d'), chartConfig);
};
